import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import type {
  CompanyBranding,
  CompanyProfile,
  CustomerDto,
  CustomerReceiptDto,
  SalesInvoiceDto,
  SalesNoteDto,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, UnauthorizedError } from './api';
import { amountInArabicWords, readableTextOn } from './brand';
import { formatMoney } from './money';

/** ما تحتاجه الترويسة: بيانات الشركة القانونية، وهويتها، واللوجو كعنوان blob محلى. */
export interface CompanyIdentity {
  profile: CompanyProfile;
  branding: CompanyBranding;
  logoUrl: string | null;
}

export interface PrintLine {
  description: string;
  quantity: string;
  unitPrice: string;
  net: string;
  tax: string;
}

/** مستند قابل للطباعة بشكل واحد — الفاتورة والإشعار والإيصال تُحوَّل إليه. */
export interface PrintableDocument {
  title: string;
  number: string;
  fields: { label: string; value: string }[];
  party: { label: string; name: string; details: string[] };
  currency: string;
  lines: PrintLine[] | null;
  totals: { label: string; value: string; grand?: boolean }[];
  /** المبلغ الذى يُكتب بالحروف — الإجمالى عادة. */
  amountInWords: string | null;
  note: string | null;
  cancelled: boolean;
  /** تذييل الفاتورة (شروط الدفع وبيانات البنك) — للفاتورة والإشعار لا للإيصال. */
  showFooter: boolean;
  /** معرّف المنظومة: الـ QR لا يظهر إلا به (BLOCKED-TAX حتى يُعتمد الإرسال الفعلى). */
  eInvoice: { uuid: string; qrPayload: string } | null;
}

/**
 * الهوية تُحمَّل مرة لكل شركة وتُشارك بين الشاشات، واللوجو يتجدد ببصمته فقط —
 * فتح عشر فواتير لا يجلب الصورة عشر مرات.
 */
const identityCache = new Map<string, Promise<CompanyIdentity>>();

export function invalidateCompanyIdentity(companyId: string): void {
  identityCache.delete(companyId);
}

function loadIdentity(companyId: string): Promise<CompanyIdentity> {
  let cached = identityCache.get(companyId);
  if (!cached) {
    cached = Promise.all([api.companySettings(companyId), api.companyBranding(companyId)]).then(
      async ([settings, branding]) => {
        const logo = branding.logo ? await api.companyLogo(companyId) : null;
        return {
          profile: settings.profile,
          branding: branding.branding,
          logoUrl: logo ? URL.createObjectURL(logo) : null,
        };
      },
    );
    cached.catch(() => identityCache.delete(companyId));
    identityCache.set(companyId, cached);
  }
  return cached;
}

export function useCompanyIdentity(
  companyId: string,
  onSessionLost: () => void,
): CompanyIdentity | null {
  const [identity, setIdentity] = useState<CompanyIdentity | null>(null);
  useEffect(() => {
    let alive = true;
    loadIdentity(companyId)
      .then((value) => {
        if (alive) setIdentity(value);
      })
      .catch((caught: unknown) => {
        if (caught instanceof UnauthorizedError) onSessionLost();
      });
    return () => {
      alive = false;
    };
  }, [companyId, onSessionLost]);
  return identity;
}

/** «2.0000» تُطبع «2» و«1.5000» تُطبع «1.5» — عرض نصى لا حساب. */
function quantity(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

function customerDetails(customer: CustomerDto | undefined): string[] {
  if (!customer) return [];
  return [
    'كود العميل: ' + customer.code,
    customer.taxRegistrationNumber ? 'الرقم الضريبى: ' + customer.taxRegistrationNumber : '',
    customer.phone ? 'تليفون: ' + customer.phone : '',
    customer.email ?? '',
  ].filter(Boolean);
}

function amountTotals(net: string, tax: string, total: string) {
  const rows: PrintableDocument['totals'] = [{ label: 'الإجمالى قبل الضريبة', value: net }];
  // الضريبة لا تظهر صفرًا: قبل اعتماد قواعدها لا ضريبة على المستند أصلاً (BLOCKED-TAX)
  if (!Money.of(tax).isZero()) rows.push({ label: 'ضريبة القيمة المضافة', value: tax });
  rows.push({ label: 'الإجمالى المستحق', value: total, grand: true });
  return rows;
}

export function invoiceDocument(
  invoice: SalesInvoiceDto,
  customer: CustomerDto | undefined,
): PrintableDocument {
  return {
    title: 'فاتورة مبيعات',
    number: invoice.number,
    fields: [
      { label: 'تاريخ الإصدار', value: invoice.issueDate },
      { label: 'تاريخ الاستحقاق', value: invoice.dueDate },
      { label: 'العملة', value: invoice.currency },
    ],
    party: { label: 'فاتورة إلى', name: invoice.customerName, details: customerDetails(customer) },
    currency: invoice.currency,
    lines: invoice.lines.map((line) => ({
      description: line.description,
      quantity: quantity(line.quantity),
      unitPrice: line.unitPrice,
      net: line.net,
      tax: line.taxAmount,
    })),
    totals: amountTotals(invoice.netTotal, invoice.taxTotal, invoice.total),
    amountInWords: invoice.total,
    note: invoice.memo,
    cancelled: invoice.status === 'cancelled',
    showFooter: true,
    eInvoice: null,
  };
}

export function noteDocument(
  note: SalesNoteDto,
  customer: CustomerDto | undefined,
): PrintableDocument {
  return {
    title: note.kind === 'credit' ? 'إشعار دائن' : 'إشعار مدين',
    number: note.number,
    fields: [
      { label: 'التاريخ', value: note.noteDate },
      { label: 'على الفاتورة', value: note.invoiceNumber },
      { label: 'العملة', value: note.currency },
    ],
    party: { label: 'العميل', name: note.customerName, details: customerDetails(customer) },
    currency: note.currency,
    lines: note.lines.map((line) => ({
      description: line.description,
      quantity: quantity(line.quantity),
      unitPrice: line.unitPrice,
      net: line.net,
      tax: line.taxAmount,
    })),
    totals: amountTotals(note.netTotal, note.taxTotal, note.total),
    amountInWords: note.total,
    note: 'السبب: ' + note.reason,
    cancelled: false,
    showFooter: true,
    eInvoice: null,
  };
}

export function receiptDocument(
  receipt: CustomerReceiptDto,
  customer: CustomerDto | undefined,
  currency: string,
): PrintableDocument {
  const totals: PrintableDocument['totals'] = [{ label: 'المبلغ المحصَّل', value: receipt.amount }];
  if (!Money.of(receipt.withholdingAmount).isZero()) {
    totals.push({ label: 'خصم من المنبع', value: receipt.withholdingAmount });
    totals.push({
      label: 'إجمالى ما سُدِّد من الذمة',
      value: Money.of(receipt.amount).add(receipt.withholdingAmount).toString(),
      grand: true,
    });
  } else {
    totals[0] = { ...totals[0]!, grand: true };
  }
  const allocations = receipt.allocations.map(
    (item) => item.invoiceNumber + ': ' + formatMoney(item.amount),
  );
  return {
    title: 'إيصال تحصيل',
    number: receipt.number,
    fields: [
      { label: 'التاريخ', value: receipt.receiptDate },
      ...(receipt.externalReference ? [{ label: 'المرجع', value: receipt.externalReference }] : []),
      { label: 'العملة', value: currency },
    ],
    party: { label: 'استلمنا من', name: receipt.customerName, details: customerDetails(customer) },
    currency,
    lines: null,
    totals,
    amountInWords: receipt.amount,
    note:
      [
        allocations.length ? 'سداد: ' + allocations.join('، ') : '',
        Money.of(receipt.unallocated).isPositive()
          ? 'دفعة مقدمة لم تُخصص: ' + formatMoney(receipt.unallocated)
          : '',
        receipt.memo ?? '',
      ]
        .filter(Boolean)
        .join(' • ') || null,
    cancelled: false,
    showFooter: false,
    eInvoice: null,
  };
}

function QrSlot({ payload }: { payload: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    QRCode.toDataURL(payload, { margin: 0, width: 160 })
      .then(setSrc)
      .catch(() => setSrc(null));
  }, [payload]);
  return src ? <img src={src} alt="QR الفاتورة الإلكترونية" /> : null;
}

/** صفحة A4 واحدة بهوية الشركة. القالب يغيّر التكوين، والألوان من البراند. */
export function DocumentSheet({
  doc,
  identity,
}: {
  doc: PrintableDocument;
  identity: CompanyIdentity;
}) {
  const { branding, profile, logoUrl } = identity;
  const { primary, secondary } = branding.colors;
  const style = {
    '--doc-primary': primary,
    '--doc-on-primary': readableTextOn(primary),
    '--doc-secondary': secondary,
    '--doc-on-secondary': readableTextOn(secondary),
  } as CSSProperties;
  const address = [
    branding.address.line1,
    branding.address.line2,
    [branding.address.city, branding.address.governorate].filter(Boolean).join('، '),
  ].filter(Boolean);
  const contact = [...branding.phones, branding.email ?? '', branding.website ?? ''].filter(
    Boolean,
  );
  const hasTax = doc.lines?.some((line) => !Money.of(line.tax).isZero()) ?? false;

  return (
    <article className={'doc-sheet tpl-' + branding.invoiceTemplate} style={style}>
      <header className="doc-header">
        <div className="doc-company">
          {logoUrl && <img className="doc-logo" src={logoUrl} alt="" />}
          <div>
            <h1>{profile.legalNameAr}</h1>
            {profile.legalNameEn && <p dir="ltr">{profile.legalNameEn}</p>}
            {address.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {contact.length > 0 && (
              <p className="doc-contact">
                {contact.map((item) => (
                  <span key={item} dir="ltr">
                    {item}
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>
        <div className="doc-title">
          <h2>{doc.title}</h2>
          <p className="doc-number">{doc.number}</p>
          {doc.cancelled && <p className="doc-stamp">ملغاة</p>}
        </div>
      </header>

      <div className="doc-ids">
        {profile.taxRegistrationNumber && (
          <span>رقم التسجيل الضريبى: {profile.taxRegistrationNumber}</span>
        )}
        {profile.commercialRegistryNumber && (
          <span>السجل التجارى: {profile.commercialRegistryNumber}</span>
        )}
      </div>

      <section className="doc-meta">
        <div className="doc-party">
          <span className="doc-label">{doc.party.label}</span>
          <strong>{doc.party.name}</strong>
          {doc.party.details.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>
        <dl className="doc-fields">
          {doc.fields.map((field) => (
            <div key={field.label}>
              <dt>{field.label}</dt>
              <dd>{field.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {doc.lines && (
        <table className="doc-lines">
          <thead>
            <tr>
              <th>#</th>
              <th className="doc-desc">البيان</th>
              <th>الكمية</th>
              <th>سعر الوحدة</th>
              <th>الصافى</th>
              {hasTax && <th>الضريبة</th>}
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((line, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td className="doc-desc">{line.description}</td>
                <td className="doc-num">{line.quantity}</td>
                <td className="doc-num">{formatMoney(line.unitPrice)}</td>
                <td className="doc-num">{formatMoney(line.net)}</td>
                {hasTax && <td className="doc-num">{formatMoney(line.tax)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <section className="doc-summary">
        <div className="doc-words">
          {doc.amountInWords && <p>{amountInArabicWords(doc.amountInWords, doc.currency)}</p>}
          {doc.note && <p className="doc-note">{doc.note}</p>}
        </div>
        <table className="doc-totals">
          <tbody>
            {doc.totals.map((row) => (
              <tr key={row.label} className={row.grand ? 'grand' : undefined}>
                <th>{row.label}</th>
                <td className="doc-num" dir="ltr">
                  {formatMoney(row.value)} {doc.currency}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <footer className="doc-footer">
        {doc.eInvoice && (
          <div className="doc-qr">
            <QrSlot payload={doc.eInvoice.qrPayload} />
            <span dir="ltr">{doc.eInvoice.uuid}</span>
          </div>
        )}
        <div className="doc-footer-text">
          {doc.showFooter && branding.invoiceFooter && <p>{branding.invoiceFooter}</p>}
          <div className="doc-signatures">
            <span>المستلم</span>
            <span>الختم والتوقيع</span>
          </div>
        </div>
      </footer>
    </article>
  );
}

/**
 * معاينة الطباعة فوق الشاشة. الطباعة من المتصفح كتقارير M5 — وأثناء فتح المعاينة
 * يُخفى التطبيق كله فى الطباعة فلا يخرج إلا المستند، بلا قائمة ولا أزرار.
 */
export function PrintPreview({
  companyId,
  doc,
  onClose,
  onSessionLost,
}: {
  companyId: string;
  doc: PrintableDocument;
  onClose: () => void;
  onSessionLost: () => void;
}) {
  const identity = useCompanyIdentity(companyId, onSessionLost);
  // المرجع لا الدالة فى الاعتماديات: الأب يمرر دالة جديدة كل رسم
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    document.body.classList.add('printing-document');
    const title = document.title;
    // اسم الملف المقترح عند «حفظ كـ PDF» هو عنوان الصفحة
    document.title = doc.title + ' ' + doc.number;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('printing-document');
      document.title = title;
      window.removeEventListener('keydown', onKey);
    };
  }, [doc.title, doc.number]);

  return createPortal(
    <div className="print-overlay" role="dialog" aria-modal="true" aria-label={doc.title}>
      <div className="print-toolbar no-print">
        <strong>
          {doc.title} {doc.number}
        </strong>
        <button
          type="button"
          className="primary"
          disabled={!identity}
          onClick={() => window.print()}
        >
          طباعة / PDF
        </button>
        <button type="button" className="ghost" onClick={onClose}>
          إغلاق
        </button>
      </div>
      {identity ? (
        <DocumentSheet doc={doc} identity={identity} />
      ) : (
        <p className="sub no-print">جارى تحميل بيانات الشركة…</p>
      )}
    </div>,
    document.body,
  );
}

/** معاينة حية بلا بيانات حقيقية — لشاشة الهوية قبل الحفظ. */
export const SAMPLE_DOCUMENT: PrintableDocument = {
  title: 'فاتورة مبيعات',
  number: 'INV-2026-000123',
  fields: [
    { label: 'تاريخ الإصدار', value: '2026-10-20' },
    { label: 'تاريخ الاستحقاق', value: '2026-11-19' },
    { label: 'العملة', value: 'EGP' },
  ],
  party: {
    label: 'فاتورة إلى',
    name: 'شركة النيل للتجارة',
    details: ['كود العميل: C-000001', 'الرقم الضريبى: 123456789'],
  },
  currency: 'EGP',
  lines: [
    {
      description: 'استشارات محاسبية — أكتوبر',
      quantity: '1',
      unitPrice: '12000',
      net: '12000',
      tax: '0',
    },
    { description: 'إعداد إقرار ضريبى', quantity: '2', unitPrice: '1500', net: '3000', tax: '0' },
  ],
  totals: [
    { label: 'الإجمالى قبل الضريبة', value: '15000' },
    { label: 'الإجمالى المستحق', value: '15000', grand: true },
  ],
  amountInWords: '15000',
  note: null,
  cancelled: false,
  showFooter: true,
  eInvoice: null,
};
